import React, { useState } from 'react';
import { useInventory } from '../context/InventoryContext';
import { Eye, Edit3, Trash2, X } from 'lucide-react';

export default function Transactions() {
  const { products, warehouses, transactions, addTransaction, editTransaction, deleteTransaction } = useInventory();
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  
  const [type, setType] = useState('supply');
  const [formData, setFormData] = useState(getEmptyForm());
  const [bomItems, setBomItems] = useState([]);

  function getEmptyForm() {
    return { id: null, productId: '', quantity: 0, warehouseId: '', fromWarehouseId: '', toWarehouseId: '', date: new Date().toISOString().split('T')[0] };
  }

  const openAdd = (tType) => { setType(tType); setFormData(getEmptyForm()); setBomItems([]); setIsFormOpen(true); };
  const openEdit = (t) => { setType(t.type); setFormData({...t}); setBomItems(t.bomItems || []); setIsFormOpen(true); };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trans = { ...formData, type };
    if (type === 'production') trans.bomItems = bomItems;
    
    if(formData.id) editTransaction(trans);
    else addTransaction(trans);
    
    setIsFormOpen(false);
  };

  const typeMap = { supply: { l: 'توريد', c: 'text-green-600 bg-green-50' }, sales: { l: 'مبيعات', c: 'text-red-600 bg-red-50' }, sales_return: { l: 'مرتجع مبيعات', c: 'text-orange-600 bg-orange-50' }, transfer: { l: 'تحويل', c: 'text-blue-600 bg-blue-50' }, production: { l: 'إنتاج', c: 'text-purple-600 bg-purple-50' } };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center bg-white p-4 rounded-xl border shadow-sm">
        <h2 className="text-xl font-bold">سندات الحركة</h2>
        <div className="flex gap-2">
          <button onClick={()=>openAdd('supply')} className="bg-green-600 text-white px-3 py-2 rounded text-sm">+ توريد</button>
          <button onClick={()=>openAdd('production')} className="bg-purple-600 text-white px-3 py-2 rounded text-sm">+ أمر إنتاج</button>
          <button onClick={()=>openAdd('sales')} className="bg-red-600 text-white px-3 py-2 rounded text-sm">+ مبيعات</button>
          <button onClick={()=>openAdd('sales_return')} className="bg-orange-600 text-white px-3 py-2 rounded text-sm">+ مرتجع مبيعات</button>
          <button onClick={()=>openAdd('transfer')} className="bg-blue-600 text-white px-3 py-2 rounded text-sm">+ تحويل</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-right border-collapse">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-4 border-b">رقم السند</th>
              <th className="p-4 border-b">التاريخ</th>
              <th className="p-4 border-b">نوع السند</th>
              <th className="p-4 border-b">الصنف</th>
              <th className="p-4 border-b text-center">الكمية</th>
              <th className="p-4 border-b text-center">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(t => (
              <tr key={t.id} className="border-b hover:bg-slate-50">
                <td className="p-4 font-mono font-bold text-slate-500">#{t.voucherId}</td>
                <td className="p-4 text-sm">{t.date}</td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${typeMap[t.type].c}`}>{typeMap[t.type].l}</span></td>
                <td className="p-4 font-bold">{products.find(p=>p.id == t.productId)?.name}</td>
                <td className="p-4 text-center font-mono">{t.quantity}</td>
                <td className="p-4 text-center flex justify-center gap-3">
                  <button onClick={()=>setViewModal(t)} className="text-blue-500 hover:text-blue-700"><Eye size={18}/></button>
                  <button onClick={()=>openEdit(t)} className="text-slate-500 hover:text-slate-700"><Edit3 size={18}/></button>
                  <button onClick={()=> {if(window.confirm('حذف السند؟')) deleteTransaction(t.id)}} className="text-red-400 hover:text-red-600"><Trash2 size={18}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6 border-t-8 border-slate-800">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <h3 className="text-xl font-bold">سند {typeMap[viewModal.type].l} #{viewModal.voucherId}</h3>
              <button onClick={()=>setViewModal(null)}><X className="text-slate-400 hover:text-red-500"/></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b pb-2"><span>التاريخ:</span><span className="font-bold">{viewModal.date}</span></div>
              <div className="flex justify-between border-b pb-2"><span>الصنف:</span><span className="font-bold">{products.find(p=>p.id == viewModal.productId)?.name}</span></div>
              <div className="flex justify-between border-b pb-2"><span>الكمية:</span><span className="font-bold text-lg">{viewModal.quantity}</span></div>
              {viewModal.type === 'transfer' ? (
                <div className="flex justify-between border-b pb-2 text-blue-600"><span>المسار:</span><span>{warehouses.find(w=>w.id==viewModal.fromWarehouseId)?.name} ➔ {warehouses.find(w=>w.id==viewModal.toWarehouseId)?.name}</span></div>
              ) : (
                <div className="flex justify-between border-b pb-2"><span>المستودع:</span><span className="font-bold">{warehouses.find(w=>w.id==viewModal.warehouseId)?.name}</span></div>
              )}
              {viewModal.type === 'production' && viewModal.bomItems && (
                <div className="mt-4 bg-slate-50 p-3 rounded border">
                  <h4 className="font-bold mb-2 underline">المواد الخام المستهلكة:</h4>
                  {viewModal.bomItems.map((b,i) => (
                    <div key={i} className="flex justify-between text-xs mb-1">
                      <span>{products.find(p=>p.id==b.productId)?.name}</span>
                      <span className="font-mono text-red-600">-{b.quantity} ({warehouses.find(w=>w.id==b.warehouseId)?.name})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{formData.id ? 'تعديل' : 'إصدار'} سند {typeMap[type].l}</h3>
              <button onClick={()=>setIsFormOpen(false)}><X className="text-slate-400 hover:text-red-500"/></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block font-bold text-sm mb-1">التاريخ</label><input type="date" required value={formData.date} onChange={e=>setFormData({...formData, date: e.target.value})} className="w-full border p-2 rounded"/></div>
              <div>
                <label className="block font-bold text-sm mb-1">{type === 'production' ? 'المنتج النهائي' : 'الصنف'}</label>
                <select required value={formData.productId} onChange={e=>setFormData({...formData, productId: e.target.value})} className="w-full border p-2 rounded">
                  <option value="">اختر...</option>{products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              
              {type !== 'transfer' && (
                <div><label className="block font-bold text-sm mb-1">المستودع</label><select required value={formData.warehouseId} onChange={e=>setFormData({...formData, warehouseId: e.target.value})} className="w-full border p-2 rounded"><option value="">اختر...</option>{warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
              )}
              {type === 'transfer' && (
                <div className="flex gap-2">
                  <div className="w-1/2"><label className="block font-bold text-sm mb-1">من</label><select required value={formData.fromWarehouseId} onChange={e=>setFormData({...formData, fromWarehouseId: e.target.value})} className="w-full border p-2 rounded"><option value="">اختر...</option>{warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                  <div className="w-1/2"><label className="block font-bold text-sm mb-1">إلى</label><select required value={formData.toWarehouseId} onChange={e=>setFormData({...formData, toWarehouseId: e.target.value})} className="w-full border p-2 rounded"><option value="">اختر...</option>{warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
                </div>
              )}
              
              <div><label className="block font-bold text-sm mb-1">الكمية</label><input type="number" required value={formData.quantity} onChange={e=>setFormData({...formData, quantity: Number(e.target.value)})} className="w-full border p-2 rounded"/></div>

              {type === 'production' && (
                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2"><span className="font-bold">المواد الخام المستهلكة</span><button type="button" onClick={()=>setBomItems([...bomItems, {productId:'',quantity:0,warehouseId:''}])} className="text-xs bg-slate-200 px-2 py-1 rounded">+ إضافة</button></div>
                  {bomItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <select required value={item.productId} onChange={e=>{const n=[...bomItems]; n[idx].productId=e.target.value; setBomItems(n)}} className="w-1/3 border p-1 text-xs"><option value="">الخامة...</option>{products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
                      <input type="number" required value={item.quantity} onChange={e=>{const n=[...bomItems]; n[idx].quantity=Number(e.target.value); setBomItems(n)}} className="w-1/4 border p-1 text-xs" placeholder="الكمية"/>
                      <select required value={item.warehouseId} onChange={e=>{const n=[...bomItems]; n[idx].warehouseId=e.target.value; setBomItems(n)}} className="w-1/3 border p-1 text-xs"><option value="">من مخزن...</option>{warehouses.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}</select>
                      <button type="button" onClick={()=>setBomItems(bomItems.filter((_,i)=>i!==idx))} className="text-red-500 w-8">X</button>
                    </div>
                  ))}
                </div>
              )}
              <button type="submit" className="w-full bg-slate-800 text-white py-3 rounded font-bold mt-4">حفظ السند</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
